import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type {
  GdprDeletionReason,
  GdprDeletionRequest,
  GdprExportRequest,
} from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  FileDown,
  Search,
  Shield,
  Trash2,
  XCircle,
} from "lucide-react";
import { useState } from "react";

const exportStatusColors: Record<string, string> = {
  pending:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  processing:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  expired: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const deletionStatusColors: Record<string, string> = {
  pending_approval:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  approved: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  processing:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  completed:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const deletionReasonLabels: Record<GdprDeletionReason, string> = {
  user_request: "User Request",
  gdpr_right_to_forget: "GDPR Right to Forget",
  legal_compliance: "Legal Compliance",
  legal_obligation: "Legal Obligation",
  policy_violation: "Policy Violation",
  duplicate_account: "Duplicate Account",
  other: "Other",
};

export default function GdprCompliance() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: "approve" | "reject" | "process";
    id: string;
    userId?: string;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  const canAccess =
    !!user?.roles && hasPermission(user.roles, "gdpr", "view_requests");

  const { data: exportRequests, isLoading: exportsLoading } = useQuery<
    GdprExportRequest[]
  >({
    queryKey: ["/api/gdpr/exports"],
    enabled: canAccess,
  });

  const { data: deletionRequests, isLoading: deletionsLoading } = useQuery<
    GdprDeletionRequest[]
  >({
    queryKey: ["/api/gdpr/deletions"],
    enabled: canAccess,
  });

  const createExportMutation = useMutation({
    mutationFn: (data: { userId: string; reason: string }) =>
      apiRequest("POST", "/api/gdpr/exports", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gdpr/exports"] });
      setIsExportDialogOpen(false);
      toast({ title: "Export request created" });
    },
    onError: () => {
      toast({
        title: "Failed to create export request",
        variant: "destructive",
      });
    },
  });

  const createDeletionMutation = useMutation({
    mutationFn: (data: {
      userId: string;
      reason: GdprDeletionReason;
      reasonDetails: string;
    }) => apiRequest("POST", "/api/gdpr/deletions", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gdpr/deletions"] });
      setIsDeletionDialogOpen(false);
      toast({ title: "Deletion request created" });
    },
    onError: () => {
      toast({
        title: "Failed to create deletion request",
        variant: "destructive",
      });
    },
  });

  const approveDeletionMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/gdpr/deletions/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gdpr/deletions"] });
      setConfirmAction(null);
      toast({ title: "Deletion request approved" });
    },
    onError: () => {
      toast({ title: "Failed to approve deletion", variant: "destructive" });
    },
  });

  const rejectDeletionMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiRequest("POST", `/api/gdpr/deletions/${id}/reject`, {
        rejectionReason: reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gdpr/deletions"] });
      setConfirmAction(null);
      setRejectReason("");
      toast({ title: "Deletion request rejected" });
    },
    onError: () => {
      toast({ title: "Failed to reject deletion", variant: "destructive" });
    },
  });

  const processDeletionMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/gdpr/deletions/${id}/process`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gdpr/deletions"] });
      setConfirmAction(null);
      toast({ title: "Deletion processed successfully" });
    },
    onError: () => {
      toast({ title: "Failed to process deletion", variant: "destructive" });
    },
  });

  const filteredExports = exportRequests?.filter(
    (req) =>
      req.userId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.reason.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredDeletions = deletionRequests?.filter(
    (req) =>
      req.userId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.reasonDetails.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const pendingDeletions =
    deletionRequests?.filter((d) => d.status === "pending_approval") || [];

  if (!canAccess) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Access Restricted</h3>
          <p className="text-muted-foreground mt-1">
            GDPR compliance tools are only available to Super Admins
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight font-serif">
            GDPR Compliance
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage data export and deletion requests
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setIsExportDialogOpen(true)}
            data-testid="button-new-export"
          >
            <Download className="h-4 w-4 mr-2" />
            New Export
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsDeletionDialogOpen(true)}
            data-testid="button-new-deletion"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            New Deletion
          </Button>
        </div>
      </div>

      {pendingDeletions.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <CardTitle className="text-base">Pending Approvals</CardTitle>
            </div>
            <CardDescription>
              {pendingDeletions.length} deletion request
              {pendingDeletions.length !== 1 ? "s" : ""} awaiting approval
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by user ID or reason..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="input-search-gdpr"
        />
      </div>

      <Tabs defaultValue="exports" className="space-y-4">
        <TabsList>
          <TabsTrigger value="exports" data-testid="tab-exports">
            <Download className="h-4 w-4 mr-2" />
            Exports ({exportRequests?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="deletions" data-testid="tab-deletions">
            <Trash2 className="h-4 w-4 mr-2" />
            Deletions ({deletionRequests?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exports" className="space-y-4">
          {exportsLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : filteredExports?.length === 0 ? (
            <div className="text-center py-12">
              <FileDown className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No export requests</h3>
              <p className="text-muted-foreground mt-1">
                Data export requests will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredExports?.map((request) => (
                <Card key={request.id} data-testid={`export-${request.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle className="text-base">
                            User: {request.userId.slice(0, 12)}...
                          </CardTitle>
                          <Badge className={exportStatusColors[request.status]}>
                            {request.status}
                          </Badge>
                        </div>
                        <CardDescription className="mt-1">
                          {request.reason}
                        </CardDescription>
                      </div>
                      {request.status === "completed" &&
                        request.downloadUrl && (
                          <Button variant="outline" size="sm" asChild>
                            <a
                              href={request.downloadUrl}
                              download
                              data-testid={`download-${request.id}`}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download ({request.downloadCount}/
                              {request.maxDownloads})
                            </a>
                          </Button>
                        )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Requested{" "}
                        {format(
                          new Date(request.requestedAt),
                          "MMM d, yyyy HH:mm",
                        )}
                      </div>
                      {request.downloadExpiry && (
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Expires{" "}
                          {format(
                            new Date(request.downloadExpiry),
                            "MMM d, yyyy HH:mm",
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deletions" className="space-y-4">
          {deletionsLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : filteredDeletions?.length === 0 ? (
            <div className="text-center py-12">
              <Trash2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No deletion requests</h3>
              <p className="text-muted-foreground mt-1">
                Data deletion requests will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDeletions?.map((request) => (
                <Card key={request.id} data-testid={`deletion-${request.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle className="text-base">
                            User: {request.userId.slice(0, 12)}...
                          </CardTitle>
                          <Badge
                            className={deletionStatusColors[request.status]}
                          >
                            {request.status.replace(/_/g, " ")}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {deletionReasonLabels[request.reason]}
                          </Badge>
                        </div>
                        <CardDescription className="mt-1">
                          {request.reasonDetails}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {request.status === "pending_approval" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() =>
                                setConfirmAction({
                                  type: "approve",
                                  id: request.id,
                                  userId: request.userId,
                                })
                              }
                              data-testid={`approve-${request.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setConfirmAction({
                                  type: "reject",
                                  id: request.id,
                                })
                              }
                              data-testid={`reject-${request.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                          </>
                        )}
                        {request.status === "approved" && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() =>
                              setConfirmAction({
                                type: "process",
                                id: request.id,
                                userId: request.userId,
                              })
                            }
                            data-testid={`process-${request.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Execute Deletion
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Requested{" "}
                        {format(
                          new Date(request.requestedAt),
                          "MMM d, yyyy HH:mm",
                        )}
                      </div>
                      {request.rejectionReason && (
                        <div className="text-red-600 dark:text-red-400">
                          Rejection: {request.rejectionReason}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CreateExportDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        onSubmit={(data) => createExportMutation.mutate(data)}
        isPending={createExportMutation.isPending}
      />

      <CreateDeletionDialog
        open={isDeletionDialogOpen}
        onOpenChange={setIsDeletionDialogOpen}
        onSubmit={(data) => createDeletionMutation.mutate(data)}
        isPending={createDeletionMutation.isPending}
      />

      <AlertDialog
        open={confirmAction?.type === "approve"}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are approving the deletion of user data for{" "}
              {confirmAction?.userId?.slice(0, 12)}... This action authorizes
              the permanent removal of all user data upon execution.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmAction &&
                approveDeletionMutation.mutate(confirmAction.id)
              }
              disabled={approveDeletionMutation.isPending}
            >
              {approveDeletionMutation.isPending
                ? "Approving..."
                : "Approve Deletion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={confirmAction?.type === "reject"}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmAction(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Deletion Request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this deletion request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rejectReason">Rejection Reason</Label>
            <Textarea
              id="rejectReason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this request is being rejected..."
              data-testid="input-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmAction &&
                rejectDeletionMutation.mutate({
                  id: confirmAction.id,
                  reason: rejectReason,
                })
              }
              disabled={
                rejectDeletionMutation.isPending || !rejectReason.trim()
              }
              data-testid="button-confirm-reject"
            >
              {rejectDeletionMutation.isPending
                ? "Rejecting..."
                : "Reject Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmAction?.type === "process"}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Execute Permanent Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-destructive">
                This action is irreversible.
              </span>{" "}
              All data for user {confirmAction?.userId?.slice(0, 12)}... will be
              permanently deleted. This includes account information,
              preferences, and all associated records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                confirmAction &&
                processDeletionMutation.mutate(confirmAction.id)
              }
              disabled={processDeletionMutation.isPending}
            >
              {processDeletionMutation.isPending
                ? "Processing..."
                : "Execute Deletion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateExportDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { userId: string; reason: string }) => void;
  isPending: boolean;
}) {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ userId, reason });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Data Export</DialogTitle>
          <DialogDescription>
            Create a GDPR-compliant data export for a user. The export will
            exclude journals, emotional check-ins, and AI inference data.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">User ID</Label>
            <Input
              id="userId"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID"
              required
              data-testid="input-export-user-id"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Export</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the reason for this export request..."
              required
              data-testid="input-export-reason"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-testid="button-submit-export"
            >
              {isPending ? "Creating..." : "Create Export Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateDeletionDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    userId: string;
    reason: GdprDeletionReason;
    reasonDetails: string;
  }) => void;
  isPending: boolean;
}) {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState<GdprDeletionReason>("user_request");
  const [reasonDetails, setReasonDetails] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ userId, reason, reasonDetails });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Data Deletion</DialogTitle>
          <DialogDescription>
            Create a GDPR data deletion request. This requires approval before
            execution.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deletionUserId">User ID</Label>
            <Input
              id="deletionUserId"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID"
              required
              data-testid="input-deletion-user-id"
            />
          </div>

          <div className="space-y-2">
            <Label>Deletion Reason</Label>
            <Select
              value={reason}
              onValueChange={(v) => setReason(v as GdprDeletionReason)}
            >
              <SelectTrigger data-testid="select-deletion-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user_request">User Request</SelectItem>
                <SelectItem value="gdpr_right_to_forget">
                  GDPR Right to Forget
                </SelectItem>
                <SelectItem value="legal_compliance">
                  Legal Compliance
                </SelectItem>
                <SelectItem value="policy_violation">
                  Policy Violation
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reasonDetails">Details</Label>
            <Textarea
              id="reasonDetails"
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              placeholder="Provide additional context for this deletion request..."
              required
              data-testid="input-deletion-details"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-testid="button-submit-deletion"
            >
              {isPending ? "Creating..." : "Create Deletion Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
